import { IconSignature } from '@sequencehq/core-components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import {
  Customer,
  NodeTimeline,
  Quote,
  QuotePreviewCard,
  useNodeTimelineVariant
} from 'Automations/components'
import {
  CancelledItem,
  FailedItem,
  hasCancelledTask,
  hasFailedTask
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api'
import { useMemo } from 'react'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export const QuoteRequiresApprovalExecutionSummary: ExecutionSummaryComponent =
  ({ tasks, isLatestNodeExecution }) => {
    const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
    const {
      latestTaskStartTime,
      quote,
      customer,
      quoteCreatedByEmail,
      taskFailed,
      taskCancelled
    } = useQuoteRequiresApprovalData(tasks)

    if (taskFailed) {
      return (
        <FailedItem
          label="Quote approval failed"
          timestamp={latestTaskStartTime}
          isLatestNodeExecution={isLatestNodeExecution}
        />
      )
    }

    if (taskCancelled) {
      return (
        <CancelledItem
          label="Quote approval cancelled"
          timestamp={latestTaskStartTime}
          isLatestNodeExecution={isLatestNodeExecution}
        />
      )
    }

    return (
      <>
        <NodeTimeline.Item
          icon={<IconSignature />}
          showStem={!isLatestNodeExecution}
          data-testid="automations.executionSummary.quoteRequiresApproval.quoteCreated"
        >
          <NodeTimeline.Title>
            <NodeTimeline.UserAction
              label="Quote created"
              performedBy={quoteCreatedByEmail}
              timestamp={quote?.createdAt}
            />
          </NodeTimeline.Title>
          {showPreviews &&
            (quote && customer ? (
              <QuotePreviewCard
                quote={quote}
                customer={customer}
                startCollapsed={!isLatestNodeExecution}
              />
            ) : (
              <NodeTimeline.UserAction
                label="Retrieving quote details"
                timestamp={latestTaskStartTime}
              />
            ))}
        </NodeTimeline.Item>
      </>
    )
  }

const useQuoteRequiresApprovalData = (
  tasks: ExecutionSummaryComponentProps['tasks']
) => {
  const latestTaskStartTime = useMemo(() => {
    return tasks[tasks.length - 1]?.startTime
  }, [tasks])

  const { quote, customer } = useMemo(() => {
    return {
      quote: tasks.find(
        tasks =>
          tasks.inputData.taskExecutorConfiguration?.executor ===
          TASK_EXECUTORS.quote
      )?.outputData?.quote as Quote | undefined,
      customer: tasks.find(
        tasks =>
          tasks.inputData.taskExecutorConfiguration?.executor ===
          TASK_EXECUTORS.customer
      )?.outputData?.customer as Customer | undefined
    }
  }, [tasks])

  const createdByUserQuery = useQuery(
    dashboard20240730Client.getUser,
    { id: quote?.createdBy ?? '' },
    { enabled: !!quote?.createdBy }
  )

  const quoteCreatedByEmail = createdByUserQuery.data?.email ?? ''

  const taskFailed = useMemo(() => hasFailedTask(tasks), [tasks])

  const taskCancelled = useMemo(() => hasCancelledTask(tasks), [tasks])

  return {
    latestTaskStartTime,
    quote,
    customer,
    quoteCreatedByEmail,
    taskFailed,
    taskCancelled
  }
}
